import api from './api';

export interface Permission {
  id: number;
  name: string;
  description: string;
  module: string;
  action: string;
  createdAt: string;
  updatedAt: string;
}

export interface Role {
  id: number;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  rolePermissions: {
    permission: Permission;
  }[];
  userRoles: {
    user: {
      id: number;
      username: string;
      employeeName: string;
    };
    project: {
      id: number;
      name: string;
    };
  }[];
}

export interface CreateRoleData {
  name: string;
  description?: string;
  permissionIds: number[];
}

export interface UpdateRoleData extends CreateRoleData {
  id: number;
}

export interface RoleFilters {
  search?: string;
  page?: number;
  limit?: number;
}

export interface RolesResponse {
  roles: Role[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface PermissionsResponse {
  permissions: Permission[];
  groupedPermissions: Record<string, Permission[]>;
}

class RoleService {
  async getRoles(filters: RoleFilters = {}): Promise<RolesResponse> {
    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await api.get(`/roles?${params}`);
    return response.data;
  }

  async getRoleById(id: number): Promise<Role> {
    const response = await api.get(`/roles/${id}`);
    return response.data;
  }

  async createRole(data: CreateRoleData): Promise<Role> {
    const response = await api.post('/roles', data);
    return response.data;
  }

  async updateRole(id: number, data: CreateRoleData): Promise<Role> {
    const response = await api.put(`/roles/${id}`, data);
    return response.data;
  }

  async deleteRole(id: number): Promise<void> {
    await api.delete(`/roles/${id}`);
  }

  async getPermissions(): Promise<PermissionsResponse> {
    const response = await api.get('/roles/permissions');
    return response.data;
  }

  // Utility functions
  formatRoleName(role: Role): string {
    return role.name;
  }

  getRolePermissionCount(role: Role): number {
    return role.rolePermissions?.length || 0;
  }

  getRoleUserCount(role: Role): number {
    return role.userRoles?.length || 0;
  }

  validateRoleData(data: CreateRoleData): string[] {
    const errors: string[] = [];

    if (!data.name?.trim()) {
      errors.push('Role name is required');
    }

    if (data.name && data.name.trim().length < 2) {
      errors.push('Role name must be at least 2 characters long');
    }

    if (data.name && data.name.trim().length > 50) {
      errors.push('Role name cannot exceed 50 characters');
    }

    if (data.description && data.description.length > 255) {
      errors.push('Description cannot exceed 255 characters');
    }

    if (!data.permissionIds || data.permissionIds.length === 0) {
      errors.push('At least one permission must be selected');
    }

    return errors;
  }

  getPermissionsByModule(permissions: Permission[]): Record<string, Permission[]> {
    return permissions.reduce((acc, permission) => {
      if (!acc[permission.module]) {
        acc[permission.module] = [];
      }
      acc[permission.module].push(permission);
      return acc;
    }, {} as Record<string, Permission[]>);
  }
}

export const roleService = new RoleService();
